/*
 * PAWFEEDER core runtime.
 * Global, dependency-free helpers shared by every template: toast messaging,
 * form busy states, the wishlist store, dialogs, share, and product context.
 * Feature specific behaviour lives in its own file (nav, product, cart).
 */
(function () {
  'use strict';

  var I18N = window.PF_I18N || {};

  var WISHLIST_KEY = 'pawfeeder-wishlist';

  function all(selector, root) { return Array.prototype.slice.call((root || document).querySelectorAll(selector)); }
  function on(selector, event, handler, root) { all(selector, root).forEach(function (node) { node.addEventListener(event, handler); }); }

  function showToast(message) {
    var toast = document.querySelector('[data-pf-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(showToast.timer);
    showToast.timer = setTimeout(function () { toast.hidden = true; }, 3600);
  }

  /*
   * Money renders in two shapes and the shop picks one globally:
   *
   *   symbol  "$12.00"   - the default, and this store's setting
   *   code    "USD 12.00" - unambiguous once USD, CAD and AUD all print "$"
   *
   * The symbol is NOT taken from Intl. Shopify resolves a symbol per presentment
   * currency and hands it down on <body data-pf-currency-symbol>; Intl would
   * print "MX$" where Shopify prints "$", so the server-rendered snippets and
   * these helpers would disagree. Reading the same value keeps them identical.
   */
  function currencySymbol() {
    return (document.body && document.body.dataset.pfCurrencySymbol) || '';
  }

  function presentmentCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
  }

  /*
   * That body symbol only fits amounts that are genuinely in the presentment
   * currency. Every price on the page is, but not every number that reaches these
   * helpers: a saved wishlist card carries the currency it was saved in, and a
   * shipping rate carries the rate's own. Printing the page symbol over those
   * would put a dollar sign on pesos, so a caller whose currency does not match
   * gets the ISO code instead - the same call the server-rendered Data Overview
   * makes, which keeps both halves of the page saying the same thing.
   */
  function prefixFor(cents, currency) {
    var active = presentmentCurrency();
    if (currency && active && currency !== active) return currency + ' ' + amountText(cents);
    return currencySymbol() + amountText(cents);
  }

  function showCurrencyCode() {
    return Boolean(document.body && document.body.dataset.pfShowCurrencyCode === 'true');
  }

  function amountText(cents) {
    var amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  }

  function money(cents, currency) {
    if (showCurrencyCode()) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency || 'USD', currencyDisplay: 'code' }).format(Number(cents || 0) / 100);
      } catch (error) {
        return (Number(cents || 0) / 100).toFixed(2);
      }
    }
    return prefixFor(cents, currency);
  }

  /*
   * Compact money for magnitude-only figures (Data Overview Total GMV). The value
   * is an estimate with error bars, so printing seven significant digits would
   * claim a precision the data does not have. Kept separate from money() because
   * prices must stay exact.
   *
   * Always K, never M: the brief is "117.3K", so 2,375,208 renders as 2375.2K.
   * useGrouping is off because a thousands separator inside a K value reads wrong.
   */
  function compactMoney(cents, currency) {
    var amount = Number(cents || 0) / 100;
    var magnitude = Math.abs(amount);
    if (magnitude < 1000) return money(cents, currency);
    var scaled = Math.round(magnitude / 100) / 10;
    if (showCurrencyCode()) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currency || 'USD',
          currencyDisplay: 'code',
          maximumFractionDigits: 1,
          minimumFractionDigits: 0,
          useGrouping: false
        }).format(scaled) + 'K';
      } catch (error) {
        return money(cents, currency);
      }
    }
    var text;
    try {
      text = new Intl.NumberFormat(document.documentElement.lang || 'en', { minimumFractionDigits: 0, maximumFractionDigits: 1, useGrouping: false }).format(scaled);
    } catch (error) {
      text = String(scaled);
    }
    var active = presentmentCurrency();
    var prefix = (currency && active && currency !== active) ? currency + ' ' : currencySymbol();
    return prefix + text + 'K';
  }

  /*
   * The symbol form of a price, regardless of the shop setting: "$12,000.00".
   * Used by the Add to cart label (CHG-2026-027), which carries words of its own
   * and has to survive a narrow viewport, so it asks for the short form
   * explicitly rather than following a setting the merchant can flip.
   */
  function moneySymbol(cents) {
    return currencySymbol() + amountText(cents);
  }

  /* ---------------------------------------------------------------- wishlist */

  function wishlist() {
    try {
      var raw = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch (error) {
      return [];
    }
  }

  function saveWishlist(handles) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles)); } catch (error) { /* storage disabled */ }
    paintWishlist(handles);
    document.dispatchEvent(new CustomEvent('pawfeeder:wishlist-change', { detail: handles }));
  }

  function paintWishlist(handles) {
    var saved = handles || wishlist();
    all('[data-pf-wishlist]').forEach(function (button) {
      var active = saved.indexOf(button.dataset.productHandle) >= 0;
      button.classList.toggle('is-saved', active);
      button.setAttribute('aria-pressed', String(active));
      var idle = button.getAttribute('data-pf-wishlist-label') || (I18N.add_to_wishlist || 'Add to wishlist');
      var activeLabel = button.getAttribute('data-pf-wishlist-saved-label') || (I18N.wishlist_saved || 'Saved');
      var next = active ? activeLabel : idle;
      /*
       * The PDP renders this control as an icon-only square, so the label sits
       * in a child node that the icon can share the button with. Anything that
       * still renders a text button keeps the old behaviour.
       */
      var label = button.querySelector('[data-pf-wishlist-text]');
      if (label) {
        label.textContent = next;
        button.setAttribute('aria-label', next);
      } else {
        button.textContent = next;
      }
    });
    all('[data-pf-wishlist-count]').forEach(function (node) {
      node.textContent = String(saved.length);
      node.hidden = saved.length === 0;
    });
  }

  on('[data-pf-wishlist]', 'click', function (event) {
    var button = event.currentTarget;
    var handle = button.dataset.productHandle;
    if (!handle) return;
    var saved = wishlist();
    var index = saved.indexOf(handle);
    var next = index >= 0 ? saved.filter(function (item) { return item !== handle; }) : saved.concat(handle);
    saveWishlist(next);
    showToast(index >= 0 ? (I18N.wishlist_removed || 'Removed from wishlist.') : (I18N.wishlist_added || 'Saved to wishlist.'));
  });

  window.addEventListener('storage', function (event) {
    if (event.key === WISHLIST_KEY) paintWishlist();
  });

  /* ------------------------------------------------------- product context */

  function currentProductContext() {
    var root = document.querySelector('[data-pf-product]');
    if (!root) return null;
    function text(selector) { var node = root.querySelector(selector); return node ? node.textContent.trim() : ''; }
    var options = all('[data-pf-option-group]', root).map(function (group) {
      var checked = group.querySelector('[data-pf-option-input]:checked');
      if (!checked) return null;
      return { name: checked.dataset.optionName, key: (checked.dataset.optionName || '').toLowerCase(), value: checked.value };
    }).filter(Boolean);

    var color = '';
    var size = '';
    options.forEach(function (option) {
      if (option.key.indexOf('color') === 0 || option.key.indexOf('colour') === 0) color = option.value;
      if (option.key.indexOf('size') === 0) size = option.value;
    });

    return {
      product: root.dataset.productTitle || '',
      sku: root.dataset.productSku || '',
      url: location.href,
      options: options.map(function (option) { return option.name + ': ' + option.value; }).join(' | '),
      color: color,
      size: size,
      pieces: text('[data-pf-pieces-label]'),
      unitPrice: text('[data-pf-current-unit-price]'),
      subtotal: text('[data-pf-estimated-subtotal]')
    };
  }

  /* -------------------------------------------------------------- dialogs */

  var INTENT_LABELS = {
    'bulk-quote': (I18N.intent_bulk_quote || 'Bulk quote'),
    'multi-product-quote': (I18N.intent_multi_product || 'Multi-product bulk quote'),
    'product-inquiry': (I18N.intent_product_inquiry || 'Product inquiry'),
    'private-label': (I18N.intent_private_label || 'Private label project'),
    'custom-styles': (I18N.intent_custom_styles || 'Custom styles / OEM')
  };
  var dialogOpeners = typeof WeakMap === 'function' ? new WeakMap() : null;

  function syncContext(dialog) {
    var context = currentProductContext();
    if (!context || !dialog) return;
    Object.keys(context).forEach(function (key) {
      all('[data-pf-context-field="' + key + '"]', dialog).forEach(function (field) { field.value = context[key]; });
    });
  }

  /**
   * Reflects the opener's enquiry intent in the dialog. Always resets first so a
   * dialog opened without an intent never keeps the previous dialog's label.
   */
  function syncIntent(dialog, opener) {
    var note = dialog.querySelector('[data-pf-intent-note]');
    if (!note) return;
    var intent = opener && opener.getAttribute('data-pf-inquiry-intent');
    if (!intent) {
      note.textContent = '';
      note.hidden = true;
      return;
    }
    note.textContent = (I18N.enquiry_type || 'Enquiry type:') + ' ' + (INTENT_LABELS[intent] || intent.replace(/-/g, ' ')) + '.';
    note.hidden = false;
  }

  function openDialog(dialog, opener) {
    if (!dialog || !dialog.showModal) return;
    syncContext(dialog);
    syncIntent(dialog, opener);
    if (dialogOpeners && opener) dialogOpeners.set(dialog, opener);
    dialog.showModal();
    var first = dialog.querySelector('input:not([type="hidden"]), select, textarea, button, a[href]');
    if (first) first.focus();
  }

  function closeDialog(dialog) {
    if (dialog && dialog.open) dialog.close();
  }

  on('[data-pf-dialog-open]', 'click', function (event) {
    var button = event.currentTarget;
    var dialog = document.getElementById(button.getAttribute('data-pf-dialog-open'));
    if (!dialog) return;
    openDialog(dialog, button);
  });

  on('[data-pf-dialog-close]', 'click', function (event) {
    closeDialog(event.currentTarget.closest('dialog'));
  });

  document.addEventListener('click', function (event) {
    if (event.target.tagName === 'DIALOG' && event.target.open) event.target.close();
  });

  all('dialog').forEach(function (dialog) {
    dialog.addEventListener('close', function () {
      var opener = dialogOpeners && dialogOpeners.get(dialog);
      if (opener && document.contains(opener)) opener.focus();
      if (dialogOpeners) dialogOpeners.delete(dialog);
    });
  });

  /* ---------------------------------------------------------------- share */

  on('[data-pf-share]', 'click', function (event) {
    var label = event.currentTarget.getAttribute('data-pf-share-label') || I18N.share || 'Share';
    if (navigator.share) {
      navigator.share({ title: document.title, url: location.href }).catch(function () { /* dismissed */ });
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(location.href)
        .then(function () { showToast(label + ': ' + (I18N.link_copied || 'product link copied.')); })
        .catch(function () { showToast((I18N.copy_manual || 'Copy the URL from your browser to share this product.')); });
    } else {
      showToast((I18N.copy_manual || 'Copy the URL from your browser to share this product.'));
    }
  });

  /* ----------------------------------------------------------- form states */

  all('form').forEach(function (form) {
    form.addEventListener('submit', function () {
      if (!form.checkValidity()) return;
      form.setAttribute('aria-busy', 'true');
      all('button[type="submit"]', form).forEach(function (button) {
        button.setAttribute('aria-disabled', 'true');
        if (!button.name) {
          button.dataset.originalLabel = button.textContent;
          button.textContent = (I18N.working || 'Working…');
        }
      });
    });
  });

  /* -------------------------------------------------------- validation copy */

  /*
   * A constraint-validation bubble is painted by the browser, in the browser's
   * own UI language - so a shopper on a zh-CN Chrome is told "请填写此字段。" by an
   * otherwise all-English storefront. Every form here (contact, ask a question,
   * bulk quote, cart quote, both newsletter forms, article comments, the
   * password page, site search and the cart note) relies on the `required` and
   * `type="email"` attributes instead of a scripted validator, which is why the
   * wording can only be taken back at the one moment the browser asks for it:
   * the `invalid` event. That event does not bubble, so the listener sits on
   * document in the capture phase; it hands the browser an English sentence
   * through setCustomValidity() before the bubble is drawn, and the wording is
   * dropped again on the next edit so the field can re-validate normally.
   * The flag on the element is what tells that wording apart from a custom
   * message some other script set on purpose.
   */
  var CUSTOM_MESSAGE_FLAG = 'pfOwnValidationMessage';

  function validationCopy(control) {
    var validity = control.validity;
    if (!validity) return '';
    if (validity.valueMissing) return (I18N.validation_value_missing || 'Please fill out this field.');
    if (validity.typeMismatch) {
      var type = String(control.getAttribute('type') || '').toLowerCase();
      if (type === 'email') return (I18N.validation_email || 'Please enter a valid email address, such as name@example.com.');
      if (type === 'url') return (I18N.validation_url || 'Please enter a full web address, starting with https://.');
      return (I18N.validation_value || 'Please enter a valid value.');
    }
    if (validity.patternMismatch) return (I18N.validation_pattern || 'Please match the format requested for this field.');
    if (validity.tooShort) return (I18N.validation_too_short || 'Please use at least @@ characters.').replace('@@', control.minLength);
    if (validity.tooLong) return (I18N.validation_too_long || 'Please shorten this to @@ characters or fewer.').replace('@@', control.maxLength);
    if (validity.badInput) return (I18N.validation_number || 'Please enter a number.');
    if (validity.rangeUnderflow) return (I18N.validation_min || 'Please enter @@ or more.').replace('@@', control.min);
    if (validity.rangeOverflow) return (I18N.validation_max || 'Please enter @@ or less.').replace('@@', control.max);
    if (validity.stepMismatch) return (I18N.validation_value || 'Please enter a valid value.');
    return (I18N.validation_check || 'Please check this field.');
  }

  function releaseValidationCopy(control) {
    if (!control || !control[CUSTOM_MESSAGE_FLAG]) return;
    control[CUSTOM_MESSAGE_FLAG] = false;
    control.setCustomValidity('');
  }

  document.addEventListener('invalid', function (event) {
    var control = event.target;
    if (!control || typeof control.setCustomValidity !== 'function') return;

    // Judge the field again from scratch. Our own wording is itself a validity
    // failure, so leaving it in place would keep a field that has since been
    // filled in (autofill, a script) looking wrong for good.
    releaseValidationCopy(control);
    if (control.checkValidity()) return;

    var copy = validationCopy(control);
    if (!copy) return;
    control[CUSTOM_MESSAGE_FLAG] = true;
    control.setCustomValidity(copy);
  }, true);

  function dropValidationCopy(event) {
    var control = event.target;
    if (!control || typeof control.setCustomValidity !== 'function') return;
    releaseValidationCopy(control);
  }

  // `input` covers typing and paste, `change` covers the selects (country,
  // language, province) whose required state is toggled at runtime.
  document.addEventListener('input', dropValidationCopy, true);
  document.addEventListener('change', dropValidationCopy, true);

  /* --------------------------------------------------------------- escape */

  /*
   * One listener for every nav item, open or not, because the mega panel is
   * dismissed by a key pressed wherever the buyer happens to be. The close was
   * already here; the focus is what this fixes. Hiding a panel that holds focus
   * drops focus to the document body - measured on the live theme, Escape on a link
   * inside the Shop panel left `activeElement` on <body> - so the keyboard buyer was
   * thrown back to the top of the page and the next Tab started again at the skip
   * link instead of continuing along the nav. Only an item that actually held focus
   * takes it back, so Escape on a hover-opened panel does not move focus at all.
   *
   * Focus first, close second, and the order is load bearing. The nav script opens a
   * mega item on `focusin`, so focusing the trigger after removing the class simply
   * reopens the panel - the restore undoes the dismissal. Focusing first means the
   * panel is already open when the class comes off, and nothing reopens it after.
   *
   * This is the only Escape handler for the mega: the nav script must not install a
   * second one per item, which would close the panel twice and leave both the focus
   * and the final state to whichever handler ran second.
   */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    all('.pf-nav__item.is-open').forEach(function (item) {
      var heldFocus = item.contains(document.activeElement);
      var trigger = item.querySelector('[aria-expanded]');
      if (heldFocus && trigger) trigger.focus();
      item.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  });

  /* --------------------------------------------------------------- export */

  paintWishlist();

  window.PawfeederUI = {
    showToast: showToast,
    money: money,
    compactMoney: compactMoney,
    moneySymbol: moneySymbol,
    wishlist: wishlist,
    saveWishlist: saveWishlist,
    paintWishlist: paintWishlist,
    currentProductContext: currentProductContext,
    syncContext: syncContext,
    openDialog: openDialog,
    closeDialog: closeDialog
  };
})();
